import React, { useState, useRef } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';

const RequestForm = ({
  dateNeeded,
  setDateNeeded,
  reason,
  setReason,
  equipmentNeeds,
  setEquipmentNeeds,
  notificationEmail,
  setNotificationEmail,
  dateError,
}) => {
  return (
    <div className="space-y-4">
      {/* Date Needed */}
      <div>
        <label className="block mb-1 text-white">Date Needed:</label>
        <input
          type="date"
          value={dateNeeded}
          onChange={(e) => setDateNeeded(e.target.value)}
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
        {dateError && <div className="text-red-500 mt-1">{dateError}</div>}
      </div>
      {/* Reason */}
      <div>
        <label className="block mb-1 text-white">Reason:</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
      </div>
      {/* Equipment Needs */}
      <div>
        <label className="block mb-1 text-white">Equipment Needs:</label>
        <div className="flex flex-wrap gap-2 p-2 bg-gray-700 rounded min-h-[50px] items-center">
          {equipmentNeeds.map((item, idx) => (
            <span
              key={idx}
              className={`px-3 py-1 rounded-full text-sm flex items-center ${
                item.includes('(Not Part of the List)') ? 'bg-red-500' : 'bg-blue-600'
              } text-white`}
            >
              {item}
              <span
                onClick={() => setEquipmentNeeds((prev) => prev.filter((_, i) => i !== idx))}
                className="ml-2 cursor-pointer"
              >
                ×
              </span>
            </span>
          ))}
          <input
            placeholder="Add more equipment"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                setEquipmentNeeds([...equipmentNeeds, e.target.value.trim()]);
                e.target.value = '';
              }
            }}
            className="flex-1 p-2 min-w-[100px] bg-gray-600 text-white rounded"
          />
        </div>
      </div>
      {/* Notification Email */}
      <div>
        <label className="block mb-1 text-white">
          Notification Email: <small>(can be changed)</small>
        </label>
        <input
          type="email"
          value={notificationEmail}
          onChange={(e) => setNotificationEmail(e.target.value)}
          className="w-full p-2 bg-gray-800 text-white rounded"
        />
      </div>
    </div>
  );
};

// Define prop types for RequestForm
RequestForm.propTypes = {
  dateNeeded: PropTypes.string,
  setDateNeeded: PropTypes.func,
  reason: PropTypes.string,
  setReason: PropTypes.func,
  equipmentNeeds: PropTypes.arrayOf(PropTypes.string),
  setEquipmentNeeds: PropTypes.func,
  notificationEmail: PropTypes.string,
  setNotificationEmail: PropTypes.func,
  dateError: PropTypes.string,
};

const EquipmentRequestModal = ({ isOpen, closeModal, onSuccessSubmit }) => {
  const [dateNeeded, setDateNeeded] = useState('');
  const [reason, setReason] = useState('');
  const [equipmentNeeds, setEquipmentNeeds] = useState([]);
  const [notificationEmail, setNotificationEmail] = useState('ahmed.mekallach@mytegroup.com');
  const [dateError, setDateError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [transcript, setTranscript] = useState('');
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const sessionIdRef = useRef(Date.now().toString());
  const transcribeQueueRef = useRef(Promise.resolve());

  const resetFormData = () => {
    setDateNeeded('');
    setReason('');
    setEquipmentNeeds([]);
    setNotificationEmail('ahmed.mekallach@mytegroup.com');
    setTranscript('');
    setRecording(false);
    setRecordingError(null);
    setMediaRecorder(null);
    setDateError('');
    setHasAttemptedSubmit(false);
  };

  const startRecording = async () => {
    console.log('Start recording clicked');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let recorderOptions = {};
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/mp4')) {
        recorderOptions.mimeType = 'audio/mp4';
      } else if (
        MediaRecorder.isTypeSupported &&
        MediaRecorder.isTypeSupported('audio/webm; codecs=opus')
      ) {
        recorderOptions.mimeType = 'audio/webm; codecs=opus';
      }
      const fileExtension = isIOS && recorderOptions.mimeType === 'audio/mp4' ? 'mp4' : 'webm';
      const recorder = new MediaRecorder(stream, recorderOptions);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          console.log('Data available: uploading chunk');
          transcribeQueueRef.current = transcribeQueueRef.current.then(() =>
            uploadAndTranscribe(e.data, fileExtension)
          );
        }
      };
      recorder.start(isIOS ? 1000 : 4000);
      setMediaRecorder(recorder);
      setRecording(true);
      console.log('Recording started');
    } catch (err) {
      console.error('Error starting recording:', err);
      setRecordingError(err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      setRecording(false);
      console.log('Recording stopped');
    }
  };

  async function uploadAndTranscribe(blob, fileExtension) {
    try {
      const formData = new FormData();
      formData.append('sessionId', sessionIdRef.current);
      formData.append('audio', blob, `chunk-${Date.now()}.${fileExtension}`);
      await fetch('/api/timesheets/upload-audio', {
        method: 'POST',
        body: formData,
      });
      console.log('Chunk uploaded');
    } catch (err) {
      console.error('Error uploading chunk:', err);
      return;
    }
    try {
      const resp = await fetch(`/api/timesheets/transcribe?sessionId=${sessionIdRef.current}`);
      const data = await resp.json();
      if (data.transcript) {
        console.log('Transcript received:', data.transcript);
        setTranscript(data.transcript);
      }
    } catch (err) {
      console.error('Error calling transcribe:', err);
    }
  }

  const analyzeConversation = async () => {
    setAnalyzing(true);
    try {
      const resp = await fetch('/api/cooper-requests/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation: transcript }),
      });
      const data = await resp.json();
      setEquipmentNeeds(data.equipmentNeeds || []);
      setDateNeeded(data.dateNeeded || '');
      setReason(data.reason || '');
      console.log('Analysis complete, updated form with:', data);
    } catch (err) {
      console.error('Error extracting data:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (!dateNeeded) {
      setDateError('You did not give us a deadline. Please choose one.');
      return;
    }
    setDateError('');
    const payload = {
      equipmentNeeds,
      dateNeeded,
      reason,
      notificationEmail,
    };
    try {
      const resp = await fetch('/api/cooper-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        if (onSuccessSubmit) {
          onSuccessSubmit();
        }
        handleClose();
      } else {
        const errRes = await resp.json();
        alert('Error: ' + errRes.error);
      }
    } catch (error) {
      console.error('Error submitting equipment request:', error);
      alert('Request failed');
    }
  };

  const cleanupSession = async () => {
    stopRecording();
    try {
      await fetch(`/api/timesheets/cleanup?sessionId=${sessionIdRef.current}`, {
        method: 'DELETE',
      });
    } catch (err) {
      console.error('Cleanup error:', err);
    }
  };

  const handleClose = () => {
    cleanupSession();
    resetFormData();
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Equipment Request"
      className="bg-gray-900 text-white w-[90%] max-w-[600px] max-h-[90vh] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-lg p-5 absolute overflow-y-auto overflow-x-hidden border border-gray-700"
      overlayClassName="fixed inset-0 bg-black/70 z-[9999]" // High z-index, full coverage
    >
      {/* Modal Header with Logo */}
      <div className="text-center mb-4">
        <img src="/LogoFile.png" alt="Myte Logo" className="max-w-[120px] mx-auto" />
      </div>

      {/* Equipment Reference and Instructions */}
      <div className="mb-4 bg-gray-800 p-3 rounded-md overflow-x-auto">
        <h3 className="text-center text-white text-xl">Equipment Reference for Ironworkers</h3>
        <p className="text-center text-white text-sm">
          Speak the equipment you need along with quantities and the deadline when you need it.
        </p>
        <table className="w-full text-white border-collapse min-w-[300px]">
          <tbody>
            <tr>
              <td className="p-1 border border-gray-600 text-sm">Hard Hat</td>
              <td className="p-1 border border-gray-600 text-sm">Harness</td>
              <td className="p-1 border border-gray-600 text-sm">Phone</td>
            </tr>
            <tr>
              <td className="p-1 border border-gray-600 text-sm">Tablet</td>
              <td className="p-1 border border-gray-600 text-sm">Laptop</td>
              <td className="p-1 border border-gray-600 text-sm">Gloves</td>
            </tr>
            <tr>
              <td className="p-1 border border-gray-600 text-sm">Safety Glasses</td>
              <td className="p-1 border border-gray-600 text-sm">Earplugs</td>
              <td className="p-1 border border-gray-600 text-sm">Steel-Toe Boots</td>
            </tr>
            <tr>
              <td className="p-1 border border-gray-600 text-sm">Welding Hoods</td>
              <td className="p-1 border border-gray-600 text-sm">Welding Gloves</td>
              <td className="p-1 border border-gray-600 text-sm">6’ Foot Level</td>
            </tr>
            <tr>
              <td className="p-1 border border-gray-600 text-sm">Body Harness</td>
              <td className="p-1 border border-gray-600 text-sm">6’ Lanyard</td>
              <td className="p-1 border border-gray-600 text-sm">FireResistant Coveralls</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Transcription Box */}
      <div className="mb-4">
        <label className="block mb-1 text-white">Transcript:</label>
        <textarea
          className="w-full h-20 bg-gray-800 text-white p-2 rounded"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
        />
      </div>

      {/* Record Button */}
      <div className="mb-4 text-center">
        <button
          onClick={recording ? stopRecording : startRecording}
          className={`py-2 px-5 rounded-md text-white cursor-pointer w-full md:w-auto ${
            recording ? 'bg-red-500' : 'bg-blue-600'
          }`}
        >
          {recording ? 'Stop Recording' : 'Start Recording'}
        </button>
      </div>
      {recordingError && (
        <div className="text-red-500 text-center mb-4">Recording Error: {recordingError}</div>
      )}

      {/* Analyze with Myte Button */}
      <div className="mb-4 text-center">
        <button
          onClick={analyzeConversation}
          className="py-2 px-5 bg-blue-700 text-white rounded-md cursor-pointer w-full md:w-auto"
        >
          Create with Myte
        </button>
      </div>

      {/* Request Form */}
      <RequestForm
        dateNeeded={dateNeeded}
        setDateNeeded={setDateNeeded}
        reason={reason}
        setReason={setReason}
        equipmentNeeds={equipmentNeeds}
        setEquipmentNeeds={setEquipmentNeeds}
        notificationEmail={notificationEmail}
        setNotificationEmail={setNotificationEmail}
        dateError={hasAttemptedSubmit ? dateError : ''}
      />

      {/* Submit for Approval Button */}
      <div className="mt-4 text-center">
        <button
          onClick={handleSubmit}
          className="py-2 px-5 bg-green-500 text-white rounded-md cursor-pointer w-full md:w-auto"
        >
          Submit for Approval
        </button>
      </div>

      {/* Cancel Button */}
      <div className="mt-4 text-center">
        <button
          onClick={handleClose}
          className="py-2 px-5 bg-gray-600 text-white rounded-md cursor-pointer w-full md:w-auto"
        >
          Cancel
        </button>
      </div>

      {/* "I am Thinking" Overlay */}
      {analyzing && (
        <div className="absolute top-0 left-0 w-full h-full bg-black/70 flex items-center justify-center z-50">
          <div className="text-white text-2xl animate-pulse">I am Thinking...</div>
        </div>
      )}
    </Modal>
  );
};

// Define prop types for EquipmentRequestModal
EquipmentRequestModal.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  onSuccessSubmit: PropTypes.func,
};

export default EquipmentRequestModal;